import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '../../../components/ui/Drawer';
import TabBar from '../../../components/TabBar';
import { useDrawerResize } from '../../../hooks/useDrawerResize';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { PermissionAccessProvider, PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import safetySystemService from '../../../services/safetySystem.service';
import type {
  SafetyDocumentTable, SafetyTableChildSpec, SafetyTableField, SafetyTableOptions, SafetyTableRecord, SafetyTableSaveBody, SafetyTableSpec,
} from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import ChildRowsEditor from './ChildRowsEditor';
import DriverPicker from './DriverPicker';
import ElementsEditor from './ElementsEditor';
import { DiscardFooter, ReadOnly, ResizeHandle, SafetyHistoryTab, errorText } from './safetyDrawerShared';
import { DateField, DateTimeField, Field, MaskedSsnField, MoneyField, SelectField, TextField, YesNoField, inputClass, str } from './unitFields';
import {
  type ChildFormRow, type FormValues, SENSITIVE_COMPONENT_ID, changedRow, childDiff, displayValue, elementsPayload, initialValues, isEditableField,
  lookupOptions, newElementRows, norm, ratingErrors, requiredErrors, rowsDirty, selectOptionsFor, tableComponentId, toInputDateTime, wireValue, withChildKeys,
} from './tableForm';

/**
 * Safety Tables drawer — the V1 Safety "Table Maintenance" edit form (Phase 5), driven
 * entirely by the catalog spec: one input per field by kind, one tab per child, History,
 * and Documents when the spec says so. `recno === 'new'` creates.
 *
 * Save posts `{row: <changed editable fields>, children: {<child>: rows}}` — read-only
 * fields are never echoed (the engine 422s them) and `create_only` fields only travel on
 * create. `fixed` pre-fills and locks fields (a retention-tab survey passes `{driverrecno}`)
 * and is always part of a create body. The Tax ID (`ssn`) is masked; Reveal / Edit need
 * `safety-system-payroll-sensitive` level 2 on top of the table's own level 2.
 *
 * The load effect is keyed on `tableKey` / `recno` only: a catalog refresh (new but equal
 * `spec` object), a re-rendered inline `fixed` literal, a late `options` object or an
 * `includeDeleted` flip never reload the form and wipe in-progress edits.
 *
 * Everything the drawer needs comes from its OWN table's payloads (grant = that table only):
 * a New drawer without `options` fetches them with `optionsOnly` (no grid rows), and a New
 * survey builds its element rows from `options.categories_by_type` rather than reading
 * `surveytype` (which 403s for a user holding only the survey grant).
 */

/** The only table with a sensitive (`ssn`) field and a reveal route. */
const REVEAL_TABLE = 'owner';

export interface SafetyTableDrawerProps {
  tableKey: string;
  recno: number | 'new';
  /** The catalog entry; when omitted the drawer loads the catalog and finds `tableKey` itself. */
  spec?: SafetyTableSpec;
  onClose: () => void;
  /** Called after a successful create / update with the record's recno (the grid refetches). */
  onSaved: (recno: number) => void;
  /** Pre-filled, locked field values (included in a create body). */
  fixed?: Record<string, unknown>;
  /** The grid load's `options` payload; while absent / empty a New drawer fetches the table once, options-only. */
  options?: SafetyTableOptions;
  /** The grid's "Include deleted" state: the row load then returns soft-deleted child rows too (with Restore). */
  includeDeleted?: boolean;
}

/** Unmanaged `elements`-style children: the field whose value picks the `options.categories_by_type` bucket for a NEW record. */
const ELEMENT_TYPE_FIELDS: Record<string, string> = { survey: 'surveytyperecno' };

/** `dbtable` labels for the History tab (children that log under their own table). */
const CHILD_HISTORY_LABELS: Record<string, string> = { contactinfo: 'Contact', owpermit_countymap: 'County' };

const EMPTY_SPEC: SafetyTableSpec = {
  key: '', title: '', table: '', level: 0, readonly: true, no_create: true, external_route: null, documents: false, soft_delete: null, label_col: null,
  fields: [], computed: [], children: [],
};

const hasOptions = (o: SafetyTableOptions | undefined): o is SafetyTableOptions => !!o && Object.keys(o).length > 0;

export default function SafetyTableDrawer({
  tableKey, recno, spec: specProp, onClose, onSaved, fixed: fixedProp, options: optionsProp, includeDeleted = false,
}: SafetyTableDrawerProps) {
  const isNew = recno === 'new';
  const tableLevel = useResolvedAccessLevel(tableComponentId(tableKey), true);
  const sensitiveLevel = useResolvedAccessLevel(SENSITIVE_COMPONENT_ID, true);
  const { width, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen: true, storageKey: 'safetyTableDrawerWidth', minWidth: 480, defaultWidth: 960,
  });

  // `fixed` is compared by content so an inline literal from the caller is stable.
  const fixedKey = JSON.stringify(fixedProp ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fixed = useMemo(() => fixedProp, [fixedKey]);

  // The spec: the caller's, or the catalog entry fetched here (Task 7 callers pass only the key).
  const [specState, setSpecState] = useState<SafetyTableSpec | null>(null);
  const [specError, setSpecError] = useState('');
  useEffect(() => {
    if (specProp) return undefined;
    const controller = new AbortController();
    setSpecError('');
    safetySystemService.tablesCatalog(controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        const found = (res.data ?? []).find((t) => t.key === tableKey) ?? null;
        setSpecState(found);
        if (!found) setSpecError('You do not have access to this table.');
      })
      .catch((e) => { if (!controller.signal.aborted) setSpecError(errorText(e)); });
    return () => controller.abort();
  }, [specProp, tableKey]);
  const spec = specProp ?? specState ?? EMPTY_SPEC;
  const specReady = !!(specProp ?? specState);
  const canEdit = specReady && tableLevel >= 2 && !spec.readonly;
  const canSensitive = canEdit && sensitiveLevel >= 2;
  const canReveal = tableKey === REVEAL_TABLE && sensitiveLevel >= 2 && !isNew;

  const [detail, setDetail] = useState<SafetyTableRecord | null>(null);
  const [options, setOptions] = useState<SafetyTableOptions>(optionsProp ?? {});
  const lookups = useMemo(() => lookupOptions(options), [options]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('info');

  const [values, setValues] = useState<FormValues>({});
  const [childRows, setChildRows] = useState<Record<string, ChildFormRow[]>>({});
  const [elements, setElements] = useState<Record<string, unknown>[]>([]);
  const [ssnValue, setSsnValue] = useState<string | null>(null);
  const [revealed, setRevealed] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => { if (contentRef.current) setPortalContainer(contentRef.current.closest('dialog') || null); }, [detail]);

  // Options that arrive after the drawer opened (deep link while the grid load was in flight).
  useEffect(() => { if (hasOptions(optionsProp)) setOptions(optionsProp); }, [optionsProp]);

  const managedChildren = useMemo(() => spec.children.filter((c) => c.managed), [spec]);
  const unmanagedChild: SafetyTableChildSpec | null = useMemo(() => spec.children.find((c) => !c.managed) ?? null, [spec]);
  const ssnField = useMemo(() => spec.fields.find((f) => f.kind === 'ssn') ?? null, [spec]);
  const elementTypeField = unmanagedChild ? ELEMENT_TYPE_FIELDS[tableKey] : undefined;

  const resetForm = useCallback((d: SafetyTableRecord | null) => {
    const base = initialValues(spec, d ? d.row : null);
    if (!d && fixed) for (const [k, v] of Object.entries(fixed)) base[k] = v;
    setValues(base);
    const rows: Record<string, ChildFormRow[]> = {};
    for (const c of spec.children) if (c.managed) rows[c.name] = withChildKeys(d?.children?.[c.name] ?? []);
    setChildRows(rows);
    setElements(unmanagedChild ? (d?.children?.[unmanagedChild.name] ?? []).map((r) => ({ ...r })) : []);
    setSsnValue(null);
    setRevealed('');
    setSaveError('');
    setConfirmDiscard(false);
  }, [spec, fixed, unmanagedChild]);
  // Latest-value refs for the load effect below (assigned in an effect, never during render).
  const resetFormRef = useRef(resetForm);
  const optionsRef = useRef(optionsProp);
  const includeDeletedRef = useRef(includeDeleted);
  useEffect(() => { resetFormRef.current = resetForm; }, [resetForm]);
  useEffect(() => { optionsRef.current = optionsProp; }, [optionsProp]);
  useEffect(() => { includeDeletedRef.current = includeDeleted; }, [includeDeleted]);

  // Load the record (or a blank form + options for a New drawer) once the spec is known —
  // keyed on the record identity only (see the module comment).
  useEffect(() => {
    if (!specReady) return undefined;
    setActiveTab('info');
    const controller = new AbortController();
    if (isNew) {
      setDetail(null);
      resetFormRef.current(null);
      setLoading(false);
      setLoadError('');
      if (hasOptions(optionsRef.current)) { setOptions(optionsRef.current); return undefined; }
      safetySystemService.tableRows(tableKey, { optionsOnly: true }, controller.signal)
        .then((res) => { if (!controller.signal.aborted) setOptions(res.options ?? {}); })
        .catch(() => { /* the form still works; lookups show what they can */ });
      return () => controller.abort();
    }
    setLoading(true);
    setLoadError('');
    safetySystemService.tableRow(tableKey, recno, { includeDeleted: includeDeletedRef.current }, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setDetail(d);
        setOptions(hasOptions(d.options) ? d.options : optionsRef.current ?? {});
        resetFormRef.current(d);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setDetail(null);
        setLoadError(errorText(e));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [tableKey, recno, isNew, specReady]);

  // New survey: the element rows come from the chosen type's active categories, which travel
  // in this table's own `options.categories_by_type` (no `surveytype` read, no extra grant).
  const typeValue = elementTypeField ? norm(values[elementTypeField]) : '';
  useEffect(() => {
    if (!isNew || !elementTypeField || !unmanagedChild) return undefined;
    setElements(!typeValue || typeValue === '0' ? [] : newElementRows(options, typeValue));
    return undefined;
  }, [isNew, elementTypeField, unmanagedChild, typeValue, options]);

  const baseValues = useMemo(() => {
    const base = initialValues(spec, detail ? detail.row : null);
    if (!detail && fixed) for (const [k, v] of Object.entries(fixed)) base[k] = v;
    return base;
  }, [spec, detail, fixed]);
  const baseElements = useMemo(() => (unmanagedChild ? detail?.children?.[unmanagedChild.name] ?? [] : []), [detail, unmanagedChild]);
  const elementsDirty = unmanagedChild ? JSON.stringify(elementsPayload(elements)) !== JSON.stringify(elementsPayload(baseElements)) : false;

  const dirty = useMemo(() => {
    if (isNew) return true;
    if (Object.keys(changedRow(spec, baseValues, values, false)).length) return true;
    if (ssnValue) return true;
    for (const c of managedChildren) if (rowsDirty(c, detail?.children?.[c.name] ?? [], childRows[c.name] ?? [])) return true;
    return elementsDirty;
  }, [isNew, spec, baseValues, values, ssnValue, managedChildren, detail, childRows, elementsDirty]);

  /**
   * Whether the user typed anything: on edit = `dirty`; on New every editable field
   * (`create_only` included, `fixed` excluded) compared to its blank, any child row, a
   * typed Tax ID, or an element rating / comment that moved off its default.
   */
  const hasEdits = (): boolean => {
    if (!isNew) return dirty;
    for (const f of spec.fields) {
      if (!isEditableField(f, true) || (fixed && f.name in fixed)) continue;
      if (norm(wireValue(f, values[f.name])) !== norm(wireValue(f, baseValues[f.name]))) return true;
    }
    for (const c of managedChildren) if ((childRows[c.name] ?? []).length) return true;
    if (ssnValue) return true;
    return elements.some((r) => Number(norm(r.elementrating) || '0') !== 0 || norm(r.elementcomments) !== '');
  };

  const setValue = (name: string, v: unknown, label?: string) => {
    setValues((p) => (label === undefined ? { ...p, [name]: v } : { ...p, [name]: v, [`${name}_label`]: label }));
    setSaveError('');
  };

  const buildBody = (): SafetyTableSaveBody => {
    const row = changedRow(spec, baseValues, values, isNew);
    if (isNew && fixed) for (const [k, v] of Object.entries(fixed)) row[k] = v;
    if (ssnField && ssnValue) row[ssnField.name] = ssnValue;
    const children: Record<string, Record<string, unknown>[]> = {};
    for (const c of managedChildren) {
      const diff = childDiff(c, detail?.children?.[c.name] ?? [], childRows[c.name] ?? []);
      if (diff.length) children[c.name] = diff;
    }
    if (unmanagedChild && elements.length) children[unmanagedChild.name] = elementsPayload(elements);
    return Object.keys(children).length ? { row, children } : { row };
  };

  const handleSave = async () => {
    if (!canEdit || saving) return;
    const problems = [...requiredErrors(spec, values, isNew), ...(unmanagedChild ? ratingErrors(elements) : [])];
    if (problems.length) { setSaveError(problems.join(' ')); return; }
    const body = buildBody();
    if (!isNew && !Object.keys(body.row).length && !body.children) { onClose(); return; }
    setSaving(true);
    setSaveError('');
    try {
      let saved: number;
      if (isNew) {
        const res = await safetySystemService.createTableRow(tableKey, body);
        saved = Number(res.recno);
      } else {
        await safetySystemService.saveTableRow(tableKey, recno, body);
        saved = recno;
      }
      onSaved(saved);
      onClose();
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const reveal = async () => {
    if (!canReveal || isNew) return;
    try {
      const res = await safetySystemService.revealOwnerSsn(recno);
      setRevealed(res.value || '—');
    } catch (e) {
      setSaveError(errorText(e));
    }
  };

  const requestClose = (): boolean => {
    if (!saving && (detail || isNew) && hasEdits()) { setConfirmDiscard(true); return false; }
    return true;
  };
  const handleCancel = () => { if (requestClose()) onClose(); };
  const handleDiscard = () => { resetForm(detail); onClose(); };

  const fetchHistory = useCallback(
    (r: number, signal?: AbortSignal) => safetySystemService.tableRowHistory(tableKey, r, signal),
    [tableKey],
  );
  const historyLabels = useMemo(() => ({ [spec.table]: spec.title, ...CHILD_HISTORY_LABELS }), [spec.table, spec.title]);

  // ---- field renderer ------------------------------------------------------
  const renderField = (f: SafetyTableField): React.ReactNode => {
    const locked = !!fixed && f.name in fixed;
    const readOnly = !canEdit || locked || !isEditableField(f, isNew);
    if (f.kind === 'ssn') {
      const last4 = norm(values[f.name]).slice(-4);
      return (
        <div key={f.name} className="space-y-1">
          <MaskedSsnField label={f.label} last4={last4} value={ssnValue} onChange={(v) => { setSsnValue(v); setSaveError(''); }} canEdit={canSensitive && !isNew} />
          {canReveal && (
            <div className="flex items-center gap-2 text-xs">
              <button type="button" onClick={() => { void reveal(); }} className="font-medium text-accent-text hover:underline" aria-label={`Reveal ${f.label}`}>Reveal</button>
              {revealed && <span aria-label={`Revealed ${f.label}`} className="tabular-nums">{revealed}</span>}
            </div>
          )}
        </div>
      );
    }
    if (!f.editable && !(f.create_only && isNew)) {
      return <ReadOnly key={f.name} label={f.label} value={displayValue(f, values)} />;
    }
    const v = values[f.name];
    switch (f.kind) {
      case 'textarea':
        return (
          <Field key={f.name} label={f.label} className="col-span-2">
            <PermissionAwareTextarea aria-label={f.label} rows={3} className={inputClass} value={str(v)} disabled={readOnly} onChange={(e) => setValue(f.name, e.target.value)} />
          </Field>
        );
      case 'int':
        return <TextField key={f.name} label={f.label} type="number" value={v} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
      case 'decimal':
        return <MoneyField key={f.name} label={f.label} value={v} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
      case 'yn':
        return <YesNoField key={f.name} label={f.label} value={v} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
      case 'date':
        return <DateField key={f.name} label={f.label} value={v} readOnly={readOnly} portalContainer={portalContainer} onChange={(x) => setValue(f.name, x)} />;
      case 'datetime':
        return <DateTimeField key={f.name} label={f.label} value={toInputDateTime(v)} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
      case 'select':
        return <SelectField key={f.name} label={f.label} value={v} options={selectOptionsFor(f, values, lookups)} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
      case 'lookup': {
        if (f.lookup === 'drivers') {
          return (
            <DriverPicker key={f.name} label={f.label} value={v} labelText={norm(values[`${f.name}_label`])} readOnly={readOnly}
              onPick={(val, lbl) => setValue(f.name, val, lbl)} />
          );
        }
        const opts = selectOptionsFor(f, values, lookups);
        return (
          <SelectField key={f.name} label={f.label} value={norm(v) === '0' ? '' : v} options={opts} readOnly={readOnly} search={opts.length > 12}
            onChange={(x) => setValue(f.name, x, opts.find((o) => o.value === x)?.label ?? '')} />
        );
      }
      default:
        return <TextField key={f.name} label={f.label} value={v} readOnly={readOnly} onChange={(x) => setValue(f.name, x)} />;
    }
  };

  // ---- chrome ---------------------------------------------------------------
  const headline = spec.label_col ? norm(baseValues[spec.label_col]) : '';
  const title = !specReady ? 'Loading…' : isNew ? `New ${spec.title}` : `${spec.title}: ${headline || `#${recno}`}`;
  const tabs = [
    { id: 'info', label: 'Info', current: activeTab === 'info' },
    ...spec.children.map((c) => ({ id: `child:${c.name}`, label: c.title, current: activeTab === `child:${c.name}` })),
    { id: 'history', label: 'History', current: activeTab === 'history', disabled: isNew },
    ...(spec.documents ? [{ id: 'documents', label: 'Documents', current: activeTab === 'documents', disabled: isNew }] : []),
  ];
  const activeChild = activeTab.startsWith('child:') ? spec.children.find((c) => `child:${c.name}` === activeTab) ?? null : null;
  const ready = specReady && !loading && (isNew || !!detail);

  const footer = confirmDiscard ? (
    <DiscardFooter onDiscard={handleDiscard} onKeep={() => setConfirmDiscard(false)} />
  ) : (
    <div className="flex items-center justify-end gap-2">
      <button type="button" onClick={handleCancel} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Cancel</button>
      {canEdit && ready && !loadError && activeTab !== 'history' && activeTab !== 'documents' && (
        <button type="button" onClick={() => { void handleSave(); }} disabled={saving || loading}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400">
          {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </button>
      )}
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} onBeforeClose={requestClose} title={title} maxWidth="full" width={`${width}px`}
      contentClassName="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6" footer={footer}>
      <ResizeHandle width={width} onHandleMouseDown={onHandleMouseDown} onHandleDoubleClick={onHandleDoubleClick} />
      <div ref={contentRef}>
        <TabBar tabs={tabs} onTabChange={setActiveTab} compact noBorder className="mb-4" />
        {specError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{specError}</div>}
        {loadError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{loadError}</div>}
        {saveError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{saveError}</div>}
        {!canEdit && ready && activeTab !== 'history' && (
          <p className="mb-3 text-xs text-muted">Read-only: you can view this record but not change it.</p>
        )}
        {(loading || (!specReady && !specError)) && <div className="py-8 text-center text-sm text-muted">Loading…</div>}

        <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
          {ready && activeTab === 'info' && (
            <div className="grid grid-cols-2 gap-2">
              {spec.fields.map(renderField)}
              {!isNew && <ReadOnly label="Record #" value={recno} />}
            </div>
          )}
          {ready && activeChild && activeChild.managed && (
            <ChildRowsEditor child={activeChild} rows={childRows[activeChild.name] ?? []} options={lookups} readOnly={!canEdit}
              onRowsChange={(rows) => { setChildRows((p) => ({ ...p, [activeChild.name]: rows })); setSaveError(''); }}
              emptyText={`No ${activeChild.title.toLowerCase()}.`} />
          )}
          {ready && activeChild && !activeChild.managed && (
            <ElementsEditor rows={elements} readOnly={!canEdit} storedRating={values.surveyrating}
              onChange={(rows) => { setElements(rows); setSaveError(''); }} />
          )}
        </PermissionAccessProvider>

        {ready && activeTab === 'history' && !isNew && (
          <SafetyHistoryTab recno={recno} fetch={fetchHistory} labels={historyLabels} emptyText="No history for this record." />
        )}
        {ready && activeTab === 'documents' && !isNew && spec.documents && (
          <SafetyDocuments tablename={spec.table as SafetyDocumentTable} tablerecno={recno} isReadOnly={!canEdit} />
        )}
      </div>
    </Drawer>
  );
}
